import { useCallback } from 'react';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';
import { ArrowLeft, ChevronDown, ChevronRight, Eye, GripVertical, Plus } from 'lucide-react';
import {
  createNewSection,
  createNewSubsection,
  useFormEditorSidebar,
  useFormMetadata,
  useFormName,
  useFormDefinition,
  useCurrentSectionIndex,
  useCurrentSubsectionIndex,
  getFormBuilderActions,
  formatConditionalBehaviorText,
} from '@/features/quote-and-apply-builder';
import {
  useSortableItem,
  DND_TYPES,
} from '@/features/quote-and-apply-builder/hooks/use-sortable-item';

interface FormEditorSidebarProps {
  onBack: () => void;
}

export function FormEditorSidebar({ onBack }: FormEditorSidebarProps) {
  const formMetadata = useFormMetadata();
  const formName = useFormName();
  const formDefinition = useFormDefinition();
  const currentSectionIndex = useCurrentSectionIndex();
  const currentSubsectionIndex = useCurrentSubsectionIndex();
  const { navigateTo, addSection, addSubsection } = getFormBuilderActions();
  const { isExpanded, toggleSection } = useFormEditorSidebar();

  return (
    <div className="sticky top-0 hidden h-screen w-80 shrink-0 overflow-y-auto border-r bg-white shadow-sm md:block">
      <div className="p-6">
        {formName && (
          <p className="text-muted-foreground mb-4 truncate px-1 text-center text-xs font-medium">
            {formName}
          </p>
        )}
        <div className="mb-6 flex items-center gap-4 p-1">
          <Button
            variant="secondary"
            onClick={onBack}
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h2 className="text-base font-semibold text-gray-950">
              {formMetadata.title || 'Untitled Form'}
            </h2>
            {formMetadata.product && (
              <p className="text-muted-foreground text-xs">{formMetadata.product}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {formDefinition.sections.map((section, sectionIndex) => (
            <SortableSectionItem
              key={section.id}
              section={section}
              sectionIndex={sectionIndex}
              isActive={sectionIndex === currentSectionIndex}
              isExpanded={isExpanded(sectionIndex)}
              currentSubsectionIndex={currentSubsectionIndex}
              questions={formDefinition.questions}
              onToggle={() => toggleSection(sectionIndex)}
              onNavigate={navigateTo}
              onAddSubsection={addSubsection}
            />
          ))}
        </div>

        <div className="mt-4 border-t pt-4">
          <div className="w-full">
            <Button
              variant="outline"
              onClick={() => addSection(createNewSection())}
              className="w-full"
            >
              <Plus />
              Add Section
            </Button>
          </div>
          <p className="text-muted-foreground mt-4 text-center text-xs">
            {formDefinition.sections.length} sections,{' '}
            {Object.keys(formDefinition.questions).length} questions
          </p>
        </div>
      </div>
    </div>
  );
}

interface SortableSectionItemProps {
  section: PolicyFormSection;
  sectionIndex: number;
  isActive: boolean;
  isExpanded: boolean;
  currentSubsectionIndex: number;
  questions: Record<string, PolicyFormQuestion>;
  onToggle: () => void;
  onNavigate: (sectionIndex: number, subsectionIndex: number) => void;
  onAddSubsection: (sectionId: string, subsection: PolicyFormSubSection) => void;
}

function SortableSectionItem({
  section,
  sectionIndex,
  isActive,
  isExpanded,
  currentSubsectionIndex,
  questions,
  onToggle,
  onNavigate,
  onAddSubsection,
}: SortableSectionItemProps) {
  const { moveSection } = getFormBuilderActions();

  const handleMoveSection = useCallback(
    (fromIndex: number, toIndex: number) => moveSection(fromIndex, toIndex),
    [moveSection],
  );

  const { itemRef, handleRef, isDragging } = useSortableItem({
    type: DND_TYPES.SECTION,
    id: section.id,
    index: sectionIndex,
    label: section.title || 'Untitled Section',
    onMove: handleMoveSection,
  });

  return (
    <div
      ref={itemRef}
      className={cn(
        'transition-opacity duration-200',
        isDragging && 'border-primary/30 bg-primary/5 rounded-lg border-2 border-dashed opacity-50',
      )}
    >
      <Collapsible
        open={isExpanded}
        onOpenChange={onToggle}
      >
        <CollapsibleTrigger asChild>
          <div
            className={cn(
              'flex w-full cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors',
              isActive ? 'border-primary/10 bg-primary/5 border' : 'hover:bg-muted',
            )}
            onClick={() => onNavigate(sectionIndex, 0)}
          >
            <div
              ref={handleRef}
              className="shrink-0 cursor-grab active:cursor-grabbing"
              onClick={e => e.stopPropagation()}
            >
              <GripVertical className="size-3.5 text-gray-400" />
            </div>
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium',
                isActive
                  ? 'border-primary text-primary border bg-white'
                  : 'bg-primary/5 text-foreground',
              )}
            >
              {sectionIndex + 1}
            </div>
            <span
              className={cn(
                'flex-1 text-left text-sm font-medium',
                isActive ? 'text-primary' : 'text-foreground',
              )}
            >
              {section.title || 'Untitled Section'}
            </span>
            {section.conditional && (
              <Tooltip>
                <TooltipTrigger
                  onClick={e => e.stopPropagation()}
                  className="shrink-0"
                >
                  <Eye className="size-3.5 text-green-600" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs p-2">
                  {formatConditionalBehaviorText(section.conditional, questions)}
                </TooltipContent>
              </Tooltip>
            )}
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
            )}
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent className="ml-9 mt-2 space-y-1">
          {section.subsections.map((subsection, subsectionIndex) => (
            <SortableSubsectionItem
              key={subsection.id}
              section={section}
              sectionIndex={sectionIndex}
              subsection={subsection}
              subsectionIndex={subsectionIndex}
              isActive={isActive && subsectionIndex === currentSubsectionIndex}
              questions={questions}
              onNavigate={() => onNavigate(sectionIndex, subsectionIndex)}
            />
          ))}
          <div
            role="button"
            className="text-muted-foreground hover:bg-muted hover:text-foreground flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm transition-colors"
            onClick={event => {
              event.stopPropagation();
              const newSubsection = createNewSubsection();
              onAddSubsection(section.id, newSubsection);
              onNavigate(sectionIndex, section.subsections.length);
            }}
          >
            <Plus className="size-3.5 shrink-0" />
            <span>Add Subsection</span>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

interface SortableSubsectionItemProps {
  section: PolicyFormSection;
  sectionIndex: number;
  subsection: PolicyFormSubSection;
  subsectionIndex: number;
  isActive: boolean;
  questions: Record<string, PolicyFormQuestion>;
  onNavigate: () => void;
}

function SortableSubsectionItem({
  section,
  subsection,
  subsectionIndex,
  isActive,
  questions,
  onNavigate,
}: SortableSubsectionItemProps) {
  const { moveSubsection } = getFormBuilderActions();

  const handleMoveSubsection = useCallback(
    (fromIndex: number, toIndex: number) => moveSubsection(section.id, fromIndex, toIndex),
    [moveSubsection, section.id],
  );

  const { itemRef, handleRef, isDragging } = useSortableItem({
    type: DND_TYPES.SUBSECTION,
    id: subsection.id,
    index: subsectionIndex,
    label: subsection.title || 'Untitled Subsection',
    onMove: handleMoveSubsection,
    containerId: section.id,
  });

  return (
    <div
      ref={itemRef}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm transition-colors',
        isActive ? 'bg-primary/5 text-primary' : 'text-foreground hover:bg-muted',
        isDragging && 'border-primary/30 bg-primary/5 rounded-md border border-dashed opacity-50',
      )}
      onClick={onNavigate}
    >
      <div
        ref={handleRef}
        className="shrink-0 cursor-grab active:cursor-grabbing"
        onClick={e => e.stopPropagation()}
      >
        <GripVertical className="size-3 text-gray-400" />
      </div>
      <div
        className={cn('h-2 w-2 shrink-0 rounded-full', isActive ? 'bg-primary' : 'bg-primary/5')}
      />
      <span className="flex-1 truncate">{subsection.title || 'Untitled Subsection'}</span>
      {subsection.conditional && (
        <Tooltip>
          <TooltipTrigger
            onClick={e => e.stopPropagation()}
            className="shrink-0"
          >
            <Eye className="size-3.5 text-green-600" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs p-2">
            {formatConditionalBehaviorText(subsection.conditional, questions)}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
